import React, { useState, useEffect } from "react";
import {MapPin, Plus, Edit2, Trash2, X, Star} from 'lucide-react';
import { lumi } from "../lib/lumi";
import taiwanCities from "../data/taiwanCities.json";

interface Address {
  _id: string;
  userEmail: string;
  recipientLastName: string;
  recipientFirstName: string;
  recipientPhone: string;
  city: string;
  district: string;
  postalCode: string;
  address: string;
  addressLabel?: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AddressManagerProps {
  currentLang: "zh" | "en";
}

const AddressManager: React.FC<AddressManagerProps> = ({ currentLang }) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [formData, setFormData] = useState({
    recipientLastName: "",
    recipientFirstName: "",
    recipientPhone: "",
    city: "",
    district: "",
    postalCode: "",
    address: "",
    addressLabel: "",
    isDefault: false,
  });

  const user = lumi.auth.user;

  const content = {
    zh: {
      title: "常用地址",
      addNew: "新增地址",
      edit: "編輯",
      delete: "刪除",
      setDefault: "設為預設",
      default: "預設地址",
      noAddress: "尚無常用地址",
      modalTitle: "新增地址",
      modalEditTitle: "編輯地址",
      recipientLastName: "收件人姓氏",
      recipientFirstName: "收件人名字",
      recipientPhone: "收件人電話",
      city: "城市",
      district: "區域",
      postalCode: "郵遞區號",
      address: "詳細地址",
      addressLabel: "地址標籤（選填）",
      labelPlaceholder: "如：家、公司",
      save: "儲存",
      cancel: "取消",
      confirmDelete: "確定要刪除此地址嗎？",
      selectCity: "請選擇城市",
      selectDistrict: "請選擇區域",
    },
    en: {
      title: "Saved Addresses",
      addNew: "Add New Address",
      edit: "Edit",
      delete: "Delete",
      setDefault: "Set as Default",
      default: "Default Address",
      noAddress: "No saved addresses yet",
      modalTitle: "Add New Address",
      modalEditTitle: "Edit Address",
      recipientLastName: "Last Name",
      recipientFirstName: "First Name",
      recipientPhone: "Phone Number",
      city: "City",
      district: "District",
      postalCode: "Postal Code",
      address: "Detailed Address",
      addressLabel: "Address Label (Optional)",
      labelPlaceholder: "e.g., Home, Office",
      save: "Save",
      cancel: "Cancel",
      confirmDelete: "Are you sure you want to delete this address?",
      selectCity: "Select City",
      selectDistrict: "Select District",
    },
  };

  const t = content[currentLang];

  // Fetch addresses
  useEffect(() => {
    fetchAddresses();
  }, [user?.email]);

  const fetchAddresses = async () => {
    if (!user?.email) return;

    setLoading(true);
    try {
      const { list } = await lumi.entities.addresses.list({
        filter: { userEmail: user.email },
        sort: { isDefault: -1, createdAt: -1 },
        limit: 100,
      });
      setAddresses(list || []);
    } catch (error) {
      console.error("Failed to fetch addresses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCityChange = (cityName: string) => {
    const selectedCity = taiwanCities.find((c) => c.name === cityName);
    setFormData({
      ...formData,
      city: cityName,
      district: "",
      postalCode: "",
    });
  };

  const handleDistrictChange = (districtName: string) => {
    const selectedCity = taiwanCities.find((c) => c.name === formData.city);
    const selectedDistrict = selectedCity?.districts.find(
      (d) => d.name === districtName
    );
    setFormData({
      ...formData,
      district: districtName,
      postalCode: selectedDistrict?.postalCode || "",
    });
  };

  const handleOpenModal = (address?: Address) => {
    if (address) {
      setEditingAddress(address);
      setFormData({
        recipientLastName: address.recipientLastName,
        recipientFirstName: address.recipientFirstName,
        recipientPhone: address.recipientPhone,
        city: address.city,
        district: address.district,
        postalCode: address.postalCode,
        address: address.address,
        addressLabel: address.addressLabel || "",
        isDefault: address.isDefault,
      });
    } else {
      setEditingAddress(null);
      setFormData({
        recipientLastName: "",
        recipientFirstName: "",
        recipientPhone: "",
        city: "",
        district: "",
        postalCode: "",
        address: "",
        addressLabel: "",
        isDefault: false,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAddress(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.email) return;

    try {
      const now = new Date().toISOString();
      const addressData = {
        ...formData,
        userEmail: user.email,
        updatedAt: now,
      };

      if (editingAddress) {
        // Update existing address
        await lumi.entities.addresses.update(editingAddress._id, addressData);
      } else {
        // Create new address
        await lumi.entities.addresses.create({
          ...addressData,
          createdAt: now,
        });
      }

      // If setting as default, unset other defaults
      if (formData.isDefault) {
        const otherAddresses = addresses.filter(
          (addr) => addr._id !== editingAddress?._id && addr.isDefault
        );
        for (const addr of otherAddresses) {
          await lumi.entities.addresses.update(addr._id, { isDefault: false });
        }
      }

      await fetchAddresses();
      handleCloseModal();
    } catch (error) {
      console.error("Failed to save address:", error);
    }
  };

  const handleDelete = async (addressId: string) => {
    if (!confirm(t.confirmDelete)) return;

    try {
      await lumi.entities.addresses.delete(addressId);
      await fetchAddresses();
    } catch (error) {
      console.error("Failed to delete address:", error);
    }
  };

  const handleSetDefault = async (addressId: string) => {
    try {
      // Unset all other defaults
      const otherAddresses = addresses.filter(
        (addr) => addr._id !== addressId && addr.isDefault
      );
      for (const addr of otherAddresses) {
        await lumi.entities.addresses.update(addr._id, { isDefault: false });
      }

      // Set selected as default
      await lumi.entities.addresses.update(addressId, { isDefault: true });
      await fetchAddresses();
    } catch (error) {
      console.error("Failed to set default address:", error);
    }
  };

  const selectedCity = taiwanCities.find((c) => c.name === formData.city);

  return (
    <>
    <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-light tracking-wide text-gray-900 dark:text-gray-100">
          {t.title}
        </h2>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center space-x-2 px-4 py-2 bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors text-sm tracking-wide"
        >
          <Plus size={16} />
          <span>{t.addNew}</span>
        </button>
      </div>

      {/* Address List */}
      {loading ? (
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
        </div>
      ) : addresses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {addresses.map((address) => (
            <div
              key={address._id}
              className={`relative border p-6 hover:border-gray-400 dark:hover:border-gray-600 transition-colors ${
                address.isDefault
                  ? "border-black dark:border-white bg-gray-50 dark:bg-gray-900"
                  : "border-gray-200 dark:border-gray-800"
              }`}
            >
              {/* Default Badge */}
              {address.isDefault && (
                <div className="absolute top-4 right-4 flex items-center space-x-1 text-xs text-yellow-600 dark:text-yellow-400">
                  <Star size={14} fill="currentColor" />
                  <span className="font-medium tracking-wide">{t.default}</span>
                </div>
              )}

              {/* Address Info */}
              <div className="space-y-3 mb-4">
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <MapPin size={16} className="text-gray-400" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {address.recipientLastName}{address.recipientFirstName}
                    </span>
                    {address.addressLabel && (
                      <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded">
                        {address.addressLabel}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 ml-6">
                    {address.recipientPhone}
                  </div>
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300 ml-6">
                  {address.postalCode} {address.city}
                  {address.district}
                  <br />
                  {address.address}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-3 ml-6">
                {!address.isDefault && (
                  <button
                    onClick={() => handleSetDefault(address._id)}
                    className="text-xs text-gray-500 hover:text-black dark:hover:text-white border-b border-gray-300 dark:border-gray-700 pb-0.5 transition-colors"
                  >
                    {t.setDefault}
                  </button>
                )}
                <button
                  onClick={() => handleOpenModal(address)}
                  className="text-xs text-gray-500 hover:text-black dark:hover:text-white border-b border-gray-300 dark:border-gray-700 pb-0.5 transition-colors"
                >
                  {t.edit}
                </button>
                <button
                  onClick={() => handleDelete(address._id)}
                  className="text-xs text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 border-b border-red-300 dark:border-red-700 pb-0.5 transition-colors"
                >
                  {t.delete}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-20 text-gray-400 dark:text-gray-600 font-light">
          <MapPin size={48} className="mx-auto mb-4 opacity-20" />
          <p>{t.noAddress}</p>
        </div>
      )}

    </div>

      {/* Modal - Rendered outside main container */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
          style={{ zIndex: 99999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={handleCloseModal}
        >
          <div
            className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 px-8 py-6 flex items-center justify-between">
              <h3 className="text-2xl font-light tracking-wide text-gray-900 dark:text-gray-100">
                {editingAddress ? t.modalEditTitle : t.modalTitle}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>

            {/* Modal Content */}
            <form onSubmit={handleSubmit} className="px-8 py-6 space-y-6">
              {/* Recipient Last Name and First Name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">
                    {t.recipientLastName}
                  </label>
                  <input
                    type="text"
                    value={formData.recipientLastName}
                    onChange={(e) =>
                      setFormData({ ...formData, recipientLastName: e.target.value })
                    }
                    required
                    className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-black dark:focus:border-white transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">
                    {t.recipientFirstName}
                  </label>
                  <input
                    type="text"
                    value={formData.recipientFirstName}
                    onChange={(e) =>
                      setFormData({ ...formData, recipientFirstName: e.target.value })
                    }
                    required
                    className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-black dark:focus:border-white transition-colors"
                  />
                </div>
              </div>

              {/* Recipient Phone */}
              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">
                  {t.recipientPhone}
                </label>
                <input
                  type="tel"
                  value={formData.recipientPhone}
                  onChange={(e) =>
                    setFormData({ ...formData, recipientPhone: e.target.value })
                  }
                  required
                  className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-black dark:focus:border-white transition-colors"
                />
              </div>

              {/* City and District */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">
                    {t.city}
                  </label>
                  <select
                    value={formData.city}
                    onChange={(e) => handleCityChange(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-black dark:focus:border-white transition-colors"
                  >
                    <option value="">{t.selectCity}</option>
                    {taiwanCities.map((city) => (
                      <option key={city.name} value={city.name}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">
                    {t.district}
                  </label>
                  <select
                    value={formData.district}
                    onChange={(e) => handleDistrictChange(e.target.value)}
                    required
                    disabled={!formData.city}
                    className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-black dark:focus:border-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">{t.selectDistrict}</option>
                    {selectedCity?.districts.map((district) => (
                      <option key={district.name} value={district.name}>
                        {district.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Postal Code (Auto-filled) */}
              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">
                  {t.postalCode}
                </label>
                <input
                  type="text"
                  value={formData.postalCode}
                  readOnly
                  className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-800 text-gray-500 cursor-not-allowed"
                />
              </div>

              {/* Detailed Address */}
              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">
                  {t.address}
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  required
                  rows={3}
                  className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-black dark:focus:border-white transition-colors resize-none"
                />
              </div>

              {/* Address Label */}
              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">
                  {t.addressLabel}
                </label>
                <input
                  type="text"
                  value={formData.addressLabel}
                  onChange={(e) =>
                    setFormData({ ...formData, addressLabel: e.target.value })
                  }
                  placeholder={t.labelPlaceholder}
                  className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:border-black dark:focus:border-white transition-colors"
                />
              </div>

              {/* Set as Default */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) =>
                    setFormData({ ...formData, isDefault: e.target.checked })
                  }
                  className="w-4 h-4 border-gray-300 dark:border-gray-700 rounded focus:ring-black dark:focus:ring-white"
                />
                <label
                  htmlFor="isDefault"
                  className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                >
                  {t.setDefault}
                </label>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors text-sm tracking-wide"
                >
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors text-sm tracking-wide"
                >
                  {t.save}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default AddressManager;